/**
 * Message bridge between the app and a sandboxed HTML frame document.
 *
 * HTML frames run agent-written HTML in a srcDoc iframe without
 * `allow-same-origin`, so the frame has an opaque origin and the app can
 * neither read its DOM nor be read by it. Everything the app needs from the
 * frame document travels over `postMessage`: the app only accepts messages
 * whose source is the frame's own window, and treats their contents as
 * untrusted input.
 */

import { readHtmlFrameEdit, type HtmlFrameEdit } from "./html-frame-edits";
import {
  readHtmlFrameHostAction,
  type HtmlFrameHostAction,
} from "./html-frame-actions";

/** App → frame: `{ type, op, requestId?, ...payload }`. */
export const FRAME_REQUEST = "denker:html-frame-request";
/** Frame → app answer to a request: `{ type, requestId, ...result }`. */
export const FRAME_REPLY = "denker:html-frame-reply";
/** Frame → app: the document's scrollable content size changed. */
export const HTML_FRAME_SIZE_MESSAGE = "denker:html-frame-size";
/** Frame → app: a right click inside the frame document. */
export const HTML_FRAME_CONTEXT_MENU_MESSAGE = "denker:html-frame-context-menu";
/** Frame → app: a trusted click on a `data-denker-action` element. */
export const HTML_FRAME_ACTION_MESSAGE = "denker:html-frame-action";

const DEFAULT_REPLY_TIMEOUT_MS = 1000;
/** A poster capture that takes longer ends quietly with no poster. */
const CAPTURE_TIMEOUT_MS = 5000;
/** Upper bound for coordinates and sizes a frame may report. */
const MAX_FRAME_PX = 100_000;

type HtmlFrameAction =
  | { kind: "link"; href: string }
  | { kind: "disclosure" }
  | { kind: "host"; action: HtmlFrameHostAction };

type HtmlFrameRequest =
  | { op: "flush" }
  | { op: "scroll-by"; left: number; top: number }
  | { op: "action-at"; x: number; y: number; activate: boolean }
  | { op: "capture"; script: string; background: string };

/** True when `event` was posted by the document inside `iframe`. */
export function isHtmlFrameMessage(
  event: MessageEvent,
  iframe: HTMLIFrameElement | null | undefined,
): iframe is HTMLIFrameElement {
  const source = iframe?.contentWindow;
  return Boolean(source) && event.source === source;
}

/** A finite pixel value in the range a frame may report, else `null`. */
export function framePx(value: unknown): number | null {
  return typeof value === "number" &&
    Number.isFinite(value) &&
    Math.abs(value) <= MAX_FRAME_PX
    ? value
    : null;
}

function postToFrame(
  iframe: HTMLIFrameElement,
  message: HtmlFrameRequest & { requestId?: string },
): boolean {
  const target = iframe.contentWindow;
  if (!target) return false;
  // An opaque-origin document cannot be addressed by origin; requests carry
  // only coordinates, deltas and the public capture library, never app data.
  target.postMessage({ type: FRAME_REQUEST, ...message }, "*");
  return true;
}

let requestSequence = 0;

function requestFromFrame(
  iframe: HTMLIFrameElement,
  request: HtmlFrameRequest,
  timeoutMs = DEFAULT_REPLY_TIMEOUT_MS,
): Promise<Record<string, unknown> | null> {
  const requestId = `${Date.now().toString(36)}-${++requestSequence}`;
  return new Promise((resolve) => {
    const finish = (reply: Record<string, unknown> | null) => {
      window.clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      resolve(reply);
    };
    const onMessage = (event: MessageEvent) => {
      if (!isHtmlFrameMessage(event, iframe)) return;
      const data = event.data as Record<string, unknown> | null;
      if (data?.type !== FRAME_REPLY || data.requestId !== requestId) return;
      finish(data);
    };
    const timer = window.setTimeout(() => finish(null), timeoutMs);
    window.addEventListener("message", onMessage);
    if (!postToFrame(iframe, { ...request, requestId })) finish(null);
  });
}

/**
 * Asks the frame for its pending inline edits and blurs its editor. Resolves
 * to `null` when nothing changed or the frame did not answer.
 */
export async function flushHtmlFrameEdit(
  iframe: HTMLIFrameElement,
): Promise<HtmlFrameEdit | null> {
  const reply = await requestFromFrame(iframe, { op: "flush" });
  return readHtmlFrameEdit(reply?.edit);
}

export function scrollHtmlFrameBy(
  iframe: HTMLIFrameElement,
  left: number,
  top: number,
): void {
  postToFrame(iframe, { op: "scroll-by", left, top });
}

/**
 * Resolves what a click at frame-local CSS pixel `(x, y)` would do. With
 * `activate`, the frame also toggles a disclosure under the point; links are
 * returned for the app to open.
 */
export async function htmlFrameActionAt(
  iframe: HTMLIFrameElement,
  x: number,
  y: number,
  activate: boolean,
): Promise<HtmlFrameAction | null> {
  const reply = await requestFromFrame(iframe, {
    op: "action-at",
    x,
    y,
    activate,
  });
  const action = reply?.action as Record<string, unknown> | null | undefined;
  if (action?.kind === "host") {
    const host = readHtmlFrameHostAction(action);
    return host ? { kind: "host", action: host } : null;
  }
  if (action?.kind === "disclosure") return { kind: "disclosure" };
  if (action?.kind === "link" && typeof action.href === "string")
    return { kind: "link", href: action.href };
  return null;
}

/** The allow-listed action a frame's action message names, else `null`. */
export function readHtmlFrameActionMessage(
  data: unknown,
): HtmlFrameHostAction | null {
  const message = data as Record<string, unknown> | null | undefined;
  if (message?.type !== HTML_FRAME_ACTION_MESSAGE) return null;
  const action = message.action as Record<string, unknown> | null | undefined;
  return action?.kind === "host" ? readHtmlFrameHostAction(action) : null;
}

/** A frame's raw poster reply: the image and what it measured, unvalidated. */
export interface HtmlFramePosterReply {
  dataUrl: string;
  /** The first screen's measure (`readFrameCoverMeasure`), untrusted. */
  measure: unknown;
}

/**
 * Asks the frame to measure its first screen and render a poster of its own
 * document with `script` (the `html-to-image` UMD build, evaluated inside
 * the frame's opaque origin) over `background`. Resolves to the frame's raw,
 * unvalidated reply; `null` when the frame answered without an image
 * (tainted canvas, failed render); `undefined` when it did not answer within
 * {@link CAPTURE_TIMEOUT_MS} (not loaded yet, or too slow).
 */
export async function captureHtmlFramePoster(
  iframe: HTMLIFrameElement,
  script: string,
  background: string,
): Promise<HtmlFramePosterReply | null | undefined> {
  const reply = await requestFromFrame(
    iframe,
    { op: "capture", script, background },
    CAPTURE_TIMEOUT_MS,
  );
  if (!reply) return undefined;
  return typeof reply.dataUrl === "string"
    ? { dataUrl: reply.dataUrl, measure: reply.measure }
    : null;
}
